// Helper type to deeply map all string values to string (not literal)
type DeepStringify<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepStringify<T[K]>;
};

export const en = {
  // Navbar
  nav: {
    findJobs: "Find Jobs",
    cvBuilder: "AI CV Builder",
    pricing: "Pricing",
    openBot: "Open Bot",
    signIn: "Sign In",
    signOut: "Sign Out",
    adminDashboard: "Admin Dashboard",
  },
  // Hero
  hero: {
    badge: "Live AI Matching",
    badgeFocus: "Worldwide",
    title: "Your AI Career Assistant",
    titleHighlight: "in Telegram",
    subtitle: "200+ commands. AI-powered CV tools. Real-time jobs from 4 sources. Instant alerts. All inside Telegram — no apps to install.",
    cta: "Open in Telegram",
    ctaPro: "Upgrade to PRO",
    leadMagnet: 'Free Guide: "How to Get a Remote Job in 14 Days"',
    statCommands: "Bot Commands",
    statSources: "Job Sources",
    statAlerts: "Alerts",
    statCV: "CV & Career",
  },
  // Features
  features: {
    title: "Everything You Need to",
    titleHighlight: "Land Your Dream Job",
    subtitle: "Powered by AI, designed for remote workers worldwide.",
    aiJobSearch: "AI Job Search",
    aiJobSearchDesc: "Real-time aggregation from 100+ sources. Find remote jobs worldwide.",
    smartMatching: "Smart Matching",
    smartMatchingDesc: "AI analyzes your profile, skills, and history to find the perfect job matches for you.",
    aiDiagnostics: "AI Diagnostics",
    aiDiagnosticsDesc: "Get detailed analysis of your CV quality, competitiveness, and market fit.",
    aiCVFix: "AI CV Fix",
    aiCVFixDesc: "Automatic improvements, ATS optimization, and job-specific adaptations.",
    cvGenerator: "CV Generator",
    cvGeneratorDesc: "Create or regenerate your CV from scratch. EN, LV, RU support with PDF/DOCX export.",
    euFocus: "Global Focus",
    euFocusDesc: "Specialized filters for work permits, languages, and region-specific requirements.",
    realtime: "Real-time Updates",
    realtimeDesc: "Live job feed with instant notifications. Never miss a matching opportunity.",
    privacy: "Privacy First",
    privacyDesc: "Your data stays secure. No paid AI credits required - fully open-source powered.",
  },
  // How It Works
  howItWorks: {
    title: "How",
    titleBrand: "RemoteAI",
    titleEnd: "Works",
    subtitle: "From Telegram to dream job in 4 simple steps",
    step1Title: "Open Telegram Bot",
    step1Desc: "Start @RemoteJobsCV_bot in Telegram. No signup needed — just press Start.",
    step2Title: "Search & Filter Jobs",
    step2Desc: "Use /jobs, /search, or 50+ tech-specific commands to find perfect remote positions.",
    step3Title: "AI Career Tools",
    step3Desc: "Get AI CV analysis, cover letters, interview prep, salary estimates — all inside Telegram.",
    step4Title: "Set Alerts & Apply",
    step4Desc: "Create /newalert for instant notifications. Upgrade to PRO for unlimited access.",
  },
  // CTA
  cta: {
    badge: "100% Free to Start",
    title: "Start Getting Jobs in Telegram Now",
    subtitle: "Join thousands of professionals worldwide. Just open the bot, type /jobs, and you're in.",
    openBot: "Open Telegram Bot",
    upgradePro: "Upgrade to PRO",
  },
  // Footer
  footer: {
    description: "The #1 AI-powered remote job platform. Find your dream job with intelligent matching.",
    product: "Product",
    resources: "Resources",
    company: "Company",
    findJobs: "Find Jobs",
    cvBuilder: "CV Builder",
    aiDiagnostics: "AI Diagnostics",
    pricing: "Pricing",
    careerTips: "Career Tips",
    interviewGuide: "Interview Guide",
    cvTemplates: "CV Templates",
    blog: "Blog",
    aboutUs: "About Us",
    supportDeveloper: "Support Developer",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    supportTitle: "Support the Developer",
    supportDesc: "Help keep RemoteAI free and open",
    copied: "Copied!",
    copyBTC: "Copy BTC",
    learnMore: "Visit our Support page",
    copyright: "© 2024 RemoteAI. All rights reserved.",
    madeFor: "Made for Everyone",
  },
  // Jobs page
  jobs: {
    title: "Find Your",
    titleHighlight: "Remote Dream Job",
    subtitle: "Real-time aggregation from",
    searchPlaceholder: "Job title, skills, or company...",
    refresh: "Refresh",
    search: "Search",
    region: "Region:",
    source: "Source:",
    allLocations: "All Locations",
    latvia: "Latvia 🇱🇻",
    eu: "EU 🇪🇺",
    worldwide: "Worldwide 🌍",
    allSources: "All Sources",
    latestJobs: "Latest Remote Jobs",
    fetchingJobs: "Fetching jobs...",
    jobsFound: "jobs found",
    showing: "Showing",
    of: "of",
    jobs: "jobs",
    loading: "Loading...",
    live: "Live",
    fetchingMultiple: "Fetching jobs from multiple sources...",
    noJobsFound: "No jobs found",
    noJobsDesc: "Try adjusting your filters or search query.",
    freePlan: "You're on the FREE plan",
    freeDesc: "Viewing {cap} of {total} jobs. Upgrade to see all results.",
    upgradePro: "Upgrade PRO — $9.99/mo",
    openBot: "Open Bot",
    allJobs: "All jobs",
    aiCVTools: "AI CV tools",
    instantAlerts: "Instant alerts",
    smartMatching: "Smart matching",
    apply: "Apply",
    prev: "Prev",
    next: "Next",
    page: "Page",
  },
  // Auth page
  auth: {
    signIn: "Sign In",
    signUp: "Sign Up",
    welcomeBack: "Welcome back! Sign in to access your account.",
    createAccount: "Create an account to start your job search journey.",
    email: "Email",
    password: "Password",
    fullName: "Full Name",
    signingIn: "Signing in...",
    creatingAccount: "Creating account...",
    forgotPassword: "Forgot your password?",
    resetPassword: "Reset your password",
    resetDescription: "Enter your email address and we'll send you a link to reset your password.",
    sendResetLink: "Send Reset Link",
    sending: "Sending...",
    cancel: "Cancel",
    terms: "By continuing, you agree to our Terms of Service and Privacy Policy.",
    missingFields: "Missing fields",
    fillRequired: "Please fill in all required fields",
    enterEmailPassword: "Please enter your email and password",
    accountExists: "Account exists",
    accountExistsDesc: "This email is already registered. Please sign in instead.",
    accountCreated: "Account created!",
    accountCreatedDesc: "You can now sign in to your account.",
    signUpFailed: "Sign up failed",
    invalidCredentials: "Invalid credentials",
    invalidCredentialsDesc: "Please check your email and password",
    welcomeBackToast: "Welcome back!",
    signedIn: "You have successfully signed in.",
    signInFailed: "Sign in failed",
    emailRequired: "Email required",
    emailRequiredDesc: "Please enter your email address first.",
    checkEmail: "Check your email",
    checkEmailDesc: "A password reset link has been sent to your email address.",
    resetFailed: "Reset failed",
    somethingWrong: "Something went wrong",
  },
  // Pricing page
  pricing: {
    badge: "Simple Pricing",
    title: "Choose Your",
    titleHighlight: "Plan",
    subtitle: "Start free in Telegram. Upgrade with USDT (TRC20) for instant activation — no KYC, lowest fees.",
    free: "Free",
    freePrice: "$0",
    forever: "forever",
    freeDesc: "Get started via Telegram",
    proMonthly: "Pro Monthly",
    proMonthlyPrice: "$9.99",
    perMonth: "/month",
    proMonthlyDesc: "Unlock all AI features",
    proYearly: "Pro Yearly",
    proYearlyPrice: "$79.99",
    perYear: "/year",
    proYearlyDesc: "Save 33% — best value",
    mostPopular: "Most Popular",
    startFree: "Start Free in Telegram",
    payUSDT: "Pay with USDT",
    payYearly: "Pay Yearly with USDT",
    features: {
      threeResults: "3 job results per search",
      basicSearch: "Basic search commands",
      browseAll: "Browse all sources",
      communitySupport: "Community support",
      unlimitedResults: "Unlimited job results",
      advancedFilters: "Advanced filters & saved searches",
      aiCVAnalysis: "AI CV Analysis & Rewrite",
      aiCoverLetter: "AI Cover Letter Generator",
      instantAlerts: "Instant job alerts",
      atsChecker: "ATS Score Checker",
      multiLangCV: "Multi-language CV (EN/LV/RU)",
      prioritySupport: "Priority support",
      everythingPro: "Everything in Pro Monthly",
      saveYearly: "Save $39.89 per year",
      twelveMonths: "12 months of full access",
      earlyAccess: "Early access to new features",
    },
    notIncluded: {
      advancedFilters: "Advanced filters",
      aiCVTools: "AI CV tools",
      instantAlerts: "Instant alerts",
      unlimitedResults: "Unlimited results",
    },
    referralTitle: "Invite Friends, Get PRO Free",
    referralDesc: "Invite 3 friends to the bot → get 7 days of PRO for free! Use",
    referralEnd: "in the bot to get your unique link.",
    usdtTitle: "Pay with USDT (TRC20)",
    usdtDesc: "We accept <b>USDT on TRON (TRC20)</b> as our primary payment method. Lowest fees, fast confirmations, and <b>automatic verification</b> — no TX hash needed.",
    signInRequired: "Sign in required",
    signInRequiredDesc: "Please sign in to make a payment.",
    copiedToClipboard: "Copied to clipboard",
    plan: "Plan",
    amount: "Amount",
    copy: "Copy",
    network: "Network",
    sendTo: "Send USDT to:",
    loadingWallet: "Loading wallet address...",
    warningNetwork: "⚠️ Send ONLY via TRON (TRC20) network. Sending via other network will result in loss of funds.",
    autoVerified: "✅ <b>Auto-Verified!</b> After sending the exact amount, your payment will be detected and confirmed automatically within 2 minutes. No TX hash required.",
    openBotStatus: "Open Bot to Check Status",
  },
  // CV Builder
  cvBuilder: {
    badge: "AI-Powered CV Generator",
    title: "Build Your",
    titleHighlight: "Perfect CV",
    titleEnd: "with AI",
    subtitle: "Create, optimize, and export professional CVs tailored for the EU remote job market. ATS-optimized and multilingual support included.",
    aiGeneration: "AI-Powered Generation",
    atsOptimized: "ATS-Optimized",
    langSupport: "EN, LV, RU Support",
    copyExport: "Copy & Export",
    step1: "Upload or Create",
    step1Desc: "Start fresh or upload existing CV",
    step2: "AI Enhancement",
    step2Desc: "AI analyzes and improves your CV",
    step3: "Review & Edit",
    step3Desc: "Fine-tune the results",
    step4: "Export",
    step4Desc: "Download in your preferred format",
    yourOptimizedCV: "Your Optimized CV",
    aiEnhanced: "AI-enhanced and ATS-ready",
    copyBtn: "Copy",
    pdf: "PDF",
    startOver: "Start Over",
    howToStart: "How would you like to start?",
    uploadExisting: "Upload Existing CV",
    uploadExistingDesc: "Upload your TXT file or paste text and let AI completely transform it.",
    createFromScratch: "Create from Scratch",
    createFromScratchDesc: "Tell us about your experience and skills, and AI will generate a professional CV.",
    tellAboutYourself: "Tell us about yourself",
    fullName: "Full Name",
    desiredPosition: "Desired Position",
    professionalSummary: "Professional Summary",
    professionalSummaryPlaceholder: "Briefly describe your professional background, key skills, and what you're looking for...",
    workExperience: "Work Experience",
    workExperiencePlaceholder: "List your relevant work experience. AI will help format and optimize it...",
    skillsAndTech: "Skills & Technologies",
    skillsPlaceholder: "List your technical skills, soft skills, languages, certifications...",
    generating: "Generating...",
    generateAICV: "Generate AI CV",
    atsOptimizedCV: "This will create an ATS-optimized CV",
    uploadYourCV: "Upload Your CV",
    back: "← Back",
    notEnoughContent: "Not enough content",
    notEnoughContentDesc: "Please provide more details (at least 50 characters).",
    cvGenerated: "CV Generated!",
    cvGeneratedDesc: "Your optimized CV is ready.",
    generationFailed: "Generation Failed",
    copied: "Copied!",
    copiedDesc: "CV copied to clipboard",
    failedCopy: "Failed to copy",
    downloaded: "Downloaded!",
    downloadedDesc: "Your CV has been saved as PDF.",
    pdfFailed: "PDF export failed",
    fileLoaded: "File loaded!",
    fileLoadedDesc: "Your CV text has been loaded.",
    unsupportedFormat: "Unsupported format",
    unsupportedFormatDesc: "Please upload a TXT file or paste your CV text directly.",
  },
  // Diagnostics
  diagnostics: {
    badge: "AI-Powered CV Analysis",
    title: "Get Your",
    titleHighlight: "CV Diagnosis",
    subtitle: "Real AI analysis of your CV quality, ATS compatibility, and personalized improvement tips.",
    uploadCV: "Upload Your CV",
    clickToUpload: "Click to upload or drag and drop",
    txtRecommended: "TXT format recommended",
    orPaste: "Or paste text directly",
    pasteCV: "Paste Your CV",
    pastePlaceholder: "Paste your CV content here... Include your experience, skills, education, and achievements.",
    uploadInstead: "Upload file instead",
    characters: "characters",
    minChars: "Minimum 100 characters",
    analyzingAI: "Analyzing with AI...",
    analyzeCV: "Analyze My CV",
    atsScoring: "ATS Scoring",
    atsDesc: "Check ATS compatibility",
    keywordAnalysis: "Keyword Analysis",
    keywordDesc: "Missing keywords",
    marketFit: "Market Fit",
    marketFitDesc: "EU job market match",
    aiRecommendations: "AI Recommendations",
    aiRecommendationsDesc: "Personalized tips",
    overallScore: "Overall ATS Score",
    outOf100: "out of 100",
    analyzeAnother: "Analyze Another CV",
    strengths: "Strengths",
    areasToImprove: "Areas to Improve",
    personalizedImprovements: "Personalized improvements",
    missingKeywords: "Missing Keywords",
    aiAutoFix: "AI Auto-Fix",
    aiAutoFixDesc: "Let AI rewrite your CV with all improvements applied",
    rewriting: "Rewriting...",
    fixMyCV: "Fix My CV with AI",
    rewrittenCV: "Rewritten CV",
    rewrittenCVDesc: "Your optimized version",
    cvTooShort: "CV too short",
    cvTooShortDesc: "Please provide more content for analysis.",
    analysisComplete: "Analysis Complete!",
    analysisCompleteDesc: "Your CV has been analyzed by AI.",
    analysisFailed: "Analysis Failed",
    rateLimited: "Rate Limited",
    rateLimitedDesc: "Too many requests. Please try again later.",
    creditsRequired: "Credits Required",
    creditsRequiredDesc: "AI credits needed. Contact support.",
    rewriteComplete: "CV Rewritten!",
    rewriteCompleteDesc: "Your optimized CV is ready.",
    rewriteFailed: "Rewrite Failed",
  },
  // Saved Jobs
  savedJobs: {
    title: "Saved",
    titleHighlight: "Jobs",
    subtitle: "Your bookmarked positions, all in one place.",
    signInToView: "Sign in to view saved jobs",
    signInDesc: "Create an account or sign in to bookmark jobs and access them anytime.",
    signIn: "Sign In",
    noSavedJobs: "No saved jobs yet",
    noSavedJobsDesc: "Browse jobs and click the heart icon to save positions you're interested in.",
    browseJobs: "Browse Jobs",
    savedJob: "Saved Job",
    savedJobs: "Saved Jobs",
    browseMore: "Browse More Jobs",
    saved: "Saved",
    apply: "Apply",
  },
  // Support page
  support: {
    title: "Support the",
    titleHighlight: "Developer",
    subtitle: "Help keep RemoteAI free and independent. Your support means everything! 🙏",
    missionTitle: "🎯 Our Mission",
    missionP1: "RemoteAI was built to help people worldwide find quality remote jobs without the noise and spam of traditional job boards. We use AI to match you with relevant opportunities and help you build a competitive CV.",
    missionP2: "This project is developed and maintained by an independent developer. Your donations help cover server costs, API fees, and allow continued development of new features.",
    btcTitle: "Donate with Bitcoin",
    btcDesc: "Privacy-preserving donations. No accounts, no tracking.",
    btcThankYou: "Every satoshi helps! Thank you for supporting open-source development.",
    otherWays: "Other Ways to Help",
    shareTwitter: "Share on Twitter",
    starGithub: "Star on GitHub",
    tellFriend: "Tell a Friend",
  },
  // 404
  notFound: {
    title: "404",
    message: "Oops! Page not found",
    goHome: "Return to Home",
  },
  // Language switcher
  langSwitcher: {
    en: "English",
    ru: "Русский",
    lv: "Latviešu",
  },
} as const;

export type TranslationKeys = DeepStringify<typeof en>;
